import { useState, useCallback, useEffect, useRef } from "react";
import { createSsnCardVerification, getSsnCardVerificationStatus } from "../../shared/api/ssnCardVerification";
import { ApiError } from "../../shared/api/verification";
import { getAllowedDomains } from "../../shared/utils/apiConfig";
import { isTokenExpiringSoon } from "../../shared/utils/tokenUtils";
import { logger } from "../../shared/utils/logger";
import { addAPIBreadcrumb, addVerificationFlowBreadcrumb } from "../../shared/utils/sentryBreadcrumbs";
import { trackFlowError } from "../../shared/utils/sentryTracking";
import { setAnalyticsIdentity, setAnalyticsEnabled, setAnalyticsTraits, resolveReferenceType, type ParentService } from "../../shared/analytics/analyticsClient";
import { trackViewed, trackOutcomePassed, trackOutcomeFailed } from "../../shared/analytics/events";
import { DOCUMENT_CAPTURE_EVENTS, DOCUMENT_CAPTURE_SECTIONS, type OutcomeSection } from "../../shared/analytics/documentCaptureEvents";
import {
  type SsnCardErrorCode,
  type SsnCardFailedApi,
  type SsnCardSuccessPayload,
  type SsnCardErrorPayload,
  type SsnCardVerificationResponse,
  SSN_CARD_CAPTURE_FEEDBACK,
} from "../../shared/types/ssnCardVerification";

export type SsnCardVerificationState =
  | "initializing"
  | "ready"
  | "capturing"
  | "processing"
  | "confirming"
  | "completed"
  | "error";

/** Maps an HTTP status code to the matching terminal SsnCardErrorCode. */
function errorCodeFromStatus(statusCode: number | undefined): SsnCardErrorCode {
  if (statusCode === 401 || statusCode === 400) return "session_invalid";
  if (statusCode !== undefined && statusCode >= 500) return "internal_error";
  return "network_error";
}

function isAllowedOrigin(origin: string): boolean {
  let hostname: string;
  try {
    hostname = new URL(origin).hostname;
  } catch {
    return false;
  }
  return getAllowedDomains().some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
}

/** Our own capture-feedback tokens, as opposed to a vendor's raw error string. */
const OWN_CAPTURE_FEEDBACK = new Set<string>(Object.values(SSN_CARD_CAPTURE_FEEDBACK));

/**
 * Outcome `reason` stays in our vocabulary: our own feedback token when we have one
 * (more specific than the code), else the code. Vendor tokens live in Sentry.
 */
function analyticsReason(errorCode: SsnCardErrorCode, feedback?: string): string {
  return feedback && OWN_CAPTURE_FEEDBACK.has(feedback) ? feedback : errorCode;
}

/**
 * Which section a failure at each stage belongs to. `null` means "emit nothing":
 * "ready"'s only failure (user_no_ssn_card) is already a Clicked, and
 * "error"/"completed" are terminal — that pair is what stops a second failure
 * emitting a second Outcome.
 */
const OUTCOME_SECTION_BY_STAGE: Record<SsnCardVerificationState, OutcomeSection | null> = {
  initializing: DOCUMENT_CAPTURE_SECTIONS.INIT,
  ready: null,
  capturing: DOCUMENT_CAPTURE_SECTIONS.DOCUMENT_CAPTURE,
  processing: DOCUMENT_CAPTURE_SECTIONS.PROCESSING,
  confirming: DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION,
  completed: null,
  error: null,
};

/** The attempt's single terminal failure Outcome, routed by the stage it failed in. */
function trackTerminalFailure(
  stage: SsnCardVerificationState,
  errorCode: SsnCardErrorCode,
  feedback?: string,
): void {
  // Stage guard first — it governs every code, including user_canceled below.
  const section = OUTCOME_SECTION_BY_STAGE[stage];
  if (!section) return;
  // Already recorded as Clicked close_document_capture.
  if (errorCode === "user_canceled") return;
  trackOutcomeFailed(section, analyticsReason(errorCode, feedback));
}

/**
 * Controller / state machine for the SSN card verification flow (iframe side).
 *
 * Single owner of `state` — the view reads it and calls the exposed intent
 * actions; it never sets state directly. Gated by `init({ confirmationScreenEnabled })`
 * (VEX-7846), a passed outcome shows the member the extracted name/SSN on a
 * confirmation screen before completing — OCR misreads (e.g. "5" read as "S")
 * would otherwise fail the backend's exact-match check with no chance for a
 * human to catch it. Confirm has no PATCH (unlike Legal ID): the backend
 * already has its answer, Confirm just proceeds with it. An un-flagged host
 * gets the pre-VEX-7846 behavior: completes immediately on a pass, no
 * confirmation screen.
 *
 * Flow: initializing -> ready -> capturing -> processing -> confirming? -> completed | error.
 */
export function useSsnCardVerification() {
  const [state, setState] = useState<SsnCardVerificationState>("initializing");
  const [error, setError] = useState<string | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [verificationResult, setVerificationResult] = useState<{ data: SsnCardVerificationResponse } | null>(null);
  // Host's rollout switch for the shutter-gated manual capture flow, from
  // `init({ manualCaptureEnabled })`. Off (auto capture) unless the host opts in.
  const [manualCaptureEnabled, setManualCaptureEnabled] = useState(false);
  // Per-launch: this is the pass after the member's retake cap is reached
  // (VEX-7961), set by IVA on this specific launch's options — not an
  // `init()`-level config, since it changes launch to launch as the retake
  // count changes. Rendered (ConfirmationScreen swaps Retake for "Join a video
  // call"), so state rather than a ref, same reasoning as manualCaptureEnabled.
  const [outOfRetakes, setOutOfRetakes] = useState(false);

  const sessionTokenRef = useRef<string | null>(null);
  const externalReferenceIdRef = useRef<string | null>(null);
  const parentOriginRef = useRef<string | null>(null);
  // Expiry is checked imperatively before API calls, not rendered.
  const tokenExpiresAtRef = useRef<number | null>(null);
  // Rollout switch for the confirmation screen (VEX-7846), from
  // `init({ confirmationScreenEnabled })`. Read imperatively in pollStatus, not
  // rendered directly, so a ref suffices — unlike `manualCaptureEnabled`, no
  // screen choice in the view depends on it. Off by default: an un-flagged host
  // gets the pre-VEX-7846 behavior (complete immediately), never a silent change.
  const confirmationScreenEnabledRef = useRef(false);
  // Latest state, readable synchronously from callbacks (e.g. to know which stage
  // a failure happened in for analytics) without stale-closure issues.
  const stateRef = useRef<SsnCardVerificationState>("initializing");

  // Breadcrumb every state transition (initializing -> ready -> ... -> completed/error),
  // mirroring legal-id's useLegalIdVerification. Keeping this in an effect (rather than
  // per-action) avoids pulling verificationId into the intent-action callback deps.
  useEffect(() => {
    addVerificationFlowBreadcrumb(state, verificationId);
  }, [state, verificationId]);

  // Sets React state AND stateRef synchronously, in the same call. stateRef alone
  // (updated only via the effect above) isn't safe for stage-routing analytics
  // decisions inside async continuations (e.g. pollStatus reading it after an
  // `await`) — the effect may not have flushed yet, so a read right after
  // `setState("processing")` could still see the previous stage. Every stage
  // transition in this hook goes through this instead of calling setState directly.
  const setFlowState = useCallback((next: SsnCardVerificationState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const postToParent = useCallback((message: object) => {
    const origin = parentOriginRef.current;
    if (!origin) {
      logger.error("[SsnCardVerification] Cannot post to parent - origin not set");
      return;
    }
    window.parent.postMessage(message, origin);
  }, []);

  const sendSuccessToParent = useCallback((payload: SsnCardSuccessPayload) => {
    postToParent({ source: "idme-verification", type: "VERIFICATION_COMPLETE", payload });
  }, [postToParent]);

  const sendErrorToParent = useCallback((payload: SsnCardErrorPayload) => {
    postToParent({ source: "idme-verification", type: "VERIFICATION_ERROR", payload });
  }, [postToParent]);

  /**
   * Enter the error state and propagate to the parent. Every failure is terminal
   * (there is no local retry loop); a capture failure carries a generic code plus a
   * specific `feedback` reason (the iProov pattern) so the parent keeps the detail.
   */
  const handleVerificationError = useCallback((
    errorCode: SsnCardErrorCode,
    message: string,
    statusCode?: number,
    responseData?: unknown,
    failedApi?: SsnCardFailedApi,
    feedback?: string,
  ) => {
    // Must run before setFlowState below: the stage the flow was in is what routes
    // the Outcome, and moving to "error" is also what stops a second failure from
    // emitting a second one.
    trackTerminalFailure(stateRef.current, errorCode, feedback);

    setFlowState("error");
    setError(message);
    logger.error(`[SsnCardVerification] Error [${errorCode}]${feedback ? ` (${feedback})` : ""}${statusCode ? ` (HTTP ${statusCode})` : ""}:`, message);
    sendErrorToParent({ errorCode, message, feedback, statusCode, failedApi, data: responseData });
  }, [sendErrorToParent]);

  /** Poll the backend until the outcome resolves. Exponential backoff, 60s cap. */
  const pollStatus = useCallback(async (id: string, token: string) => {
    const initialInterval = 2000;
    const multiplier = 1.5;
    const maxInterval = 15000;
    const timeoutMs = 60000;
    const startTime = Date.now();
    let attempt = 0;
    let lastStatusCode: number | undefined;

    addAPIBreadcrumb("GET ssn-card-verification status", "polling_start", { flow: "ssn_card" });

    try {
      while (true) {
        attempt++;

        if (isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
          handleVerificationError("token_expired", "Session token has expired", undefined, undefined, "get_ssn_card_failed");
          return;
        }

        try {
          const { data: result, statusCode } = await getSsnCardVerificationStatus(id, token);
          lastStatusCode = statusCode;
          addAPIBreadcrumb("GET ssn-card-verification status", statusCode ?? "unknown", { flow: "ssn_card", attempt, outcome: result?.outcome });

          if (
              result?.outcome === "failed" ||
              result?.outcome === "fraudulent" ||
              result?.outcome === "abandoned"
          ) {
            handleVerificationError("verification_rejected", "SSN card verification was rejected", statusCode, result, "get_ssn_card_failed");
            return;
          }

          // Verification passed. Gated on confirmationScreenEnabled (VEX-7846,
          // from init({ confirmationScreenEnabled })): flagged-in hosts see the
          // confirmation screen before anything reaches the parent; everyone else
          // gets the pre-VEX-7846 behavior — complete immediately.
          if (result?.outcome === "passed") {
            if (confirmationScreenEnabledRef.current) {
              setVerificationResult({ data: result });
              setFlowState("confirming");
            } else {
              trackOutcomePassed(DOCUMENT_CAPTURE_SECTIONS.PROCESSING);
              setVerificationResult({ data: result });
              setFlowState("completed");
              sendSuccessToParent({ data: result });
            }
            return;
          }

          // still pending
          if (Date.now() - startTime >= timeoutMs) {
            handleVerificationError("network_error", "Verification is still processing. Please try again later.", lastStatusCode, undefined, "get_ssn_card_failed");
            return;
          }
          const interval = Math.min(initialInterval * Math.pow(multiplier, attempt - 1), maxInterval);
          await new Promise((resolve) => setTimeout(resolve, interval));
        } catch (pollError) {
          const statusCode = pollError instanceof ApiError ? pollError.statusCode : undefined;
          // 401/400 — fail immediately, don't retry
          if (statusCode === 401 || statusCode === 400) throw pollError;
          if (Date.now() - startTime >= timeoutMs) throw pollError;
          const interval = Math.min(initialInterval * Math.pow(multiplier, attempt - 1), maxInterval);
          await new Promise((resolve) => setTimeout(resolve, interval));
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to retrieve verification status";
      const statusCode = err instanceof ApiError ? err.statusCode : undefined;
      const responseData = err instanceof ApiError ? err.responseData : undefined;
      handleVerificationError(errorCodeFromStatus(statusCode), message, statusCode, responseData, "get_ssn_card_failed");
    }
  }, [handleVerificationError, sendSuccessToParent]);

  /**
   * Intent action: member confirmed the extracted name/SSN are accurate. No
   * PATCH — the backend already answered `passed` in pollStatus; this just
   * proceeds with that result. Mirrors legal-id's confirmVerification, minus
   * the confirmation API call it makes (VEX-7846: no such endpoint for SSN Card).
   */
  const confirmVerification = useCallback(() => {
    if (!verificationResult) {
      handleVerificationError("confirmation_data_missing", "Missing verification data for confirmation");
      return;
    }

    trackOutcomePassed(DOCUMENT_CAPTURE_SECTIONS.CONFIRMATION);
    setFlowState("completed");
    sendSuccessToParent(verificationResult);
  }, [verificationResult, sendSuccessToParent, handleVerificationError]);

  /**
   * Handshake: validate the parent origin, signal IDME_READY, and on IDME_INIT
   * store the token and move to `ready`. Returns a listener-cleanup function.
   */
  const initialize = useCallback((): (() => void) => {
    let origin: string;
    try {
      if (!document.referrer) throw new Error("Cannot determine parent origin - document.referrer is empty");
      origin = new URL(document.referrer).origin;
      if (!isAllowedOrigin(origin)) throw new Error(`Invalid parent origin: ${origin}. Only IDMe domains are allowed.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Initialization failed";
      logger.error("[SsnCardVerification] Initialization failed:", message);

      trackFlowError(err, "invalid_parent_origin", "ssn_card", {
        cause: document.referrer ? "untrusted_origin" : "empty_referrer",
      });
      setFlowState("error");
      setError(message);
      return () => {};
    }

    parentOriginRef.current = origin;
    logger.log("[SsnCardVerification] Parent origin validated:", origin);

    const handleInitMessage = (event: MessageEvent) => {
      // Only accept the token-bearing INIT from the validated parent origin.
      if (event.origin !== origin) return;
      if (!event.data || event.data.type !== "IDME_INIT") return;

      const payload = event.data.payload;
      window.removeEventListener("message", handleInitMessage);

      const { sessionToken, tokenExpiresAt, externalReferenceId, parentService, analyticsEnabled, manualCaptureEnabled, confirmationScreenEnabled, outOfRetakes: isOutOfRetakes } = payload ?? {};

      // The host's rollout switch, from init({ analyticsEnabled }). Must run before
      // the first tracked event below (and before the two guards, which emit their
      // own Outcome). Strict `=== true` so a missing or non-boolean field stays off
      // — no signal means no tracking.
      setAnalyticsEnabled(analyticsEnabled === true);
      // Same strict `=== true`: a missing or non-boolean field stays on the
      // default (auto capture).
      const usesManualCapture = manualCaptureEnabled === true;
      setManualCaptureEnabled(usesManualCapture);
      // Same strict `=== true`: a missing or non-boolean field keeps the
      // pre-VEX-7846 behavior (pollStatus completes immediately on a pass,
      // no confirmation screen) rather than silently opting a host in.
      const showConfirmationScreen = confirmationScreenEnabled === true;
      confirmationScreenEnabledRef.current = showConfirmationScreen;
      // Same strict `=== true`: a missing or non-boolean field keeps the
      // standard confirmation screen (Retake photo), not the out-of-retakes
      // variant — no signal means no variant (VEX-7961).
      const isRetakesExhausted = isOutOfRetakes === true;
      setOutOfRetakes(isRetakesExhausted);
      // Tag every event of this launch with the capture arm and confirmation-screen
      // arm, so both can be compared directly in the warehouse without first
      // joining out to the experiment's assignment table. Set here, alongside the
      // other IDME_INIT-derived analytics state, so it lands before the
      // session-started event emitted below.
      setAnalyticsTraits({ captureMode: usesManualCapture ? "manual" : "auto", confirmationScreenEnabled: showConfirmationScreen, outOfRetakes: isRetakesExhausted });

      // Bind identity before either guard below. Both still emit an Outcome, and
      // without an identity it ships with no referenceId and no context.app.parent —
      // un-joinable to the attempt it's reporting on. The generated fallback id is
      // reused by submitImages(), so analytics and the backend always agree.
      const referenceId: string = externalReferenceId || `web_${Date.now()}`;
      externalReferenceIdRef.current = referenceId;
      const resolvedParentService: ParentService = (parentService as ParentService | undefined) ?? "iva";
      setAnalyticsIdentity({
        parentService: resolvedParentService,
        referenceType: resolveReferenceType(resolvedParentService),
        referenceId,
      });

      if (!sessionToken) {
        handleVerificationError("session_invalid", "Invalid initialization payload");
        return;
      }

      if (typeof tokenExpiresAt === "number") tokenExpiresAtRef.current = tokenExpiresAt;
      if (isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
        handleVerificationError("token_expired", "Session token has expired", undefined, undefined, "post_ssn_card_failed");
        return;
      }

      sessionTokenRef.current = sessionToken;

      trackViewed(DOCUMENT_CAPTURE_EVENTS.VIEWED.SESSION_STARTED, "section", { section: DOCUMENT_CAPTURE_SECTIONS.INIT });

      setFlowState("ready");
    };

    window.addEventListener("message", handleInitMessage);

    // Signal to the parent that we're ready to receive the token.
    window.parent.postMessage({ source: "idme-verification", type: "IDME_READY" }, origin);

    return () => {
      window.removeEventListener("message", handleInitMessage);
    };
  }, [handleVerificationError]);

  /** Intent action: advance from the info screen to the capture screen. */
  const beginCapture = useCallback(() => {
    setFlowState("capturing");
  }, []);

  /**
   * Intent action: upload the captured front + back, then poll for the result.
   * POST -> processing -> (poll) -> completed | error.
   */
  const submitImages = useCallback(async (imageFrontBase64: string, imageBackBase64: string) => {
    // Transition first so the guards below report as upload failures, not capture
    // failures — capture already succeeded. Note these two guards are synchronous,
    // so React batches this with the error state and ProcessingScreen never mounts:
    // they produce a processing Outcome with no matching Viewed.
    setFlowState("processing");

    const token = sessionTokenRef.current;
    if (!token) {
      handleVerificationError("session_invalid", "Missing session token");
      return;
    }

    if (isTokenExpiringSoon(tokenExpiresAtRef.current ?? undefined)) {
      handleVerificationError("token_expired", "Session token has expired", undefined, undefined, "post_ssn_card_failed");
      return;
    }

    let id: string;
    try {
      addAPIBreadcrumb("POST ssn-card-verification", "pending", { flow: "ssn_card" });
      const res = await createSsnCardVerification({
        sessionToken: token,
        imageFrontBase64,
        imageBackBase64,
        // Already resolved once at IDME_INIT (see handleInitMessage) — same value
        // used for the analytics identity, so the two can always be joined.
        externalReferenceId: externalReferenceIdRef.current!,
      });
      id = res.verificationId;
      setVerificationId(id);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create SSN card verification";
      const statusCode = err instanceof ApiError ? err.statusCode : undefined;
      const responseData = err instanceof ApiError ? err.responseData : undefined;
      handleVerificationError(errorCodeFromStatus(statusCode), message, statusCode, responseData, "post_ssn_card_failed");
      return;
    }

    await pollStatus(id, token);
  }, [handleVerificationError, pollStatus]);

  return {
    // state (read-only for the view)
    state,
    error,
    verificationId,
    verificationResult,
    manualCaptureEnabled,
    outOfRetakes,

    // intent actions
    initialize,
    beginCapture,
    submitImages,
    confirmVerification,
    handleVerificationError,
  };
}
